import { ArrayHelpers, Field, FieldArray, FormikProps } from "formik";
import { AccordionBody, AccordionHeader, AccordionItem } from "../../Accordion";
import { getTexts } from "../texts";
import { Heading4, Heading5 } from "../Headings";
import { FormRadio, FormRadioList } from "../../form/FormRadio";
import { TableNumbers } from "../../layouts/TableNumbers";
import ValidationError from "../../form/ValidationError";
import {
  TILSYN_EJERANDELE,
  TILSYN_VIRK_FORMAAL,
} from "../../../constants/tilsyn/lists";
import { TilsynserklaeringFormValues } from "../../../interfaces/tilsynserklaering";
import IconDelete from "../../icons/IconDelete";
import ValidatedField from "../../form/ValidatedField";
import { isOneChecked } from "../../../util/common";
import { tilsynSection2CompleteHelper } from "../../../util/tilsynserklaering/isComplete";
import IsRequired from "../../form/IsRequired";
import { FormSelect } from "../../form/FormSelect";
import { FormCheck } from "../../form/FormCheck";

const TilsynerklaeringSection2 = ({
  texts,
  props,
}: {
  texts: ReturnType<typeof getTexts>;
  props: FormikProps<TilsynserklaeringFormValues>;
}) => {
  const { values, errors, touched } = props;

  const { section2IsStarted, section2IsComplete } =
    tilsynSection2CompleteHelper(values, errors, touched);

  return (
    <AccordionItem value="2">
      <AccordionHeader
        sectionStarted={section2IsStarted}
        sectionComplete={section2IsComplete}
      >
        {texts.section2.title}
      </AccordionHeader>
      <AccordionBody>
        <div className="vstack gap-5">
          <div>
            <Heading4 className="mb-2">
              {texts.section2.section2_1.title}
            </Heading4>

            <Heading5>
              {texts.section2.section2_1.question.label} <IsRequired />
            </Heading5>
            <FormRadioList>
              {texts.section2.section2_1.question.options.map((option) => (
                <FormRadio
                  name={texts.section2.section2_1.question.id}
                  value={option.value}
                  key={option.value}
                  invalid={
                    errors?.[texts.section2.section2_1.question.id] &&
                    touched?.[texts.section2.section2_1.question.id]
                  }
                >
                  {option.label}
                </FormRadio>
              ))}
              <ValidationError
                error={errors[texts.section2.section2_1.question.id]}
                show={props.touched[texts.section2.section2_1.question.id]}
              />
            </FormRadioList>
          </div>
          {props.values.ejerandeleIAndenVirksomhed ===
            texts.section2.section2_1.question.options[0].value && (
            <div>
              <Heading4>{texts.section2.section2_2.title}</Heading4>
              <FieldArray
                name={texts.section2.section2_2.id}
                render={(arrayHelpers: ArrayHelpers) =>
                  values.tilsynEjerandele?.map((e, i) => {
                    return (
                      <div
                        className="d-flex gap-3 mt-4"
                        key={"tilsynejerandel-" + i}
                      >
                        <div className="flex-shrink-0 mt-2">
                          <TableNumbers>{(i + 1).toString()}</TableNumbers>
                        </div>
                        <div className="vstack gap-3">
                          <div className="border px-4 py-3 rounded">
                            <div className="row">
                              <div className="col">
                                <label
                                  className="form-label fw-semibold"
                                  htmlFor="virksomhedensNavn"
                                >
                                  Virksomhedens navn <IsRequired />
                                </label>
                                <ValidatedField
                                  error={
                                    errors.tilsynEjerandele?.[i]?.[
                                      "virksomhedensNavn"
                                    ]
                                  }
                                  touched={
                                    touched.tilsynEjerandele?.[i]
                                      ?.virksomhedensNavn
                                  }
                                  autoComplete="off"
                                  type="text"
                                  className={`form-control ${
                                    errors.tilsynEjerandele?.[i]?.[
                                      "virksomhedensNavn"
                                    ] &&
                                    touched.tilsynEjerandele?.[i]
                                      ?.virksomhedensNavn
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  name={`tilsynEjerandele[${i}].virksomhedensNavn`}
                                  placeholder="Virksomhedens navn"
                                  wrapper
                                />
                              </div>
                              <div className="col">
                                <label
                                  htmlFor="virksomhedensCVR"
                                  className="form-label fw-semibold"
                                >
                                  Virksomhedens CVR-nummer
                                </label>
                                <Field
                                  autoComplete="off"
                                  className={"form-control"}
                                  id="virksomhedensCVR"
                                  name={`tilsynEjerandele[${i}].virksomhedensCVR`}
                                  placeholder="CVR-nummer"
                                />
                              </div>
                            </div>
                            <Heading5 className="mt-5 fw-semibold">
                              Virksomhedens formål <IsRequired />
                            </Heading5>
                            <div className="grid gap-2">
                              {TILSYN_VIRK_FORMAAL.map((formaal, index) => (
                                <div key={index} className="g-col-6">
                                  <FormCheck
                                    id={`tilsynEjerandele[${i}].formaal-${(
                                      index + 1
                                    ).toString()}`}
                                    isInvalid={isOneChecked(
                                      "formaal-",
                                      touched.tilsynEjerandele?.[i] ?? [],
                                      e,
                                    )}
                                  >
                                    {formaal}
                                  </FormCheck>
                                </div>
                              ))}
                            </div>
                            <h5 className="mb-2 mt-5 fw-semibold">
                              Ejerandel <IsRequired />
                            </h5>
                            <div className="row">
                              <FormSelect
                                formik={props}
                                name={`tilsynEjerandele[${i}].andel`}
                                options={[
                                  ...TILSYN_EJERANDELE.map((andel) => ({
                                    value: andel,
                                    label: andel,
                                  })),
                                ]}
                                defaultValue={
                                  props.values.tilsynEjerandele?.[i]?.andel
                                }
                              />
                            </div>
                          </div>
                          {i + 1 === values.tilsynEjerandele?.length && (
                            <button
                              className="btn btn-light w-100 py-2"
                              type="button"
                              onClick={() => {
                                arrayHelpers.push({
                                  virksomhedensNavn: "",
                                  virksomhedensCVR: "",
                                  andel: "",
                                  "formaal-1": false,
                                  "formaal-2": false,
                                  "formaal-3": false,
                                  "formaal-4": false,
                                  "formaal-5": false,
                                  "formaal-6": false,
                                  "formaal-7": false,
                                  "formaal-8": false,
                                  "formaal-9": false,
                                  "formaal-10": false,
                                  "formaal-11": false,
                                  "formaal-12": false,
                                  "formaal-13": false,
                                  "formaal-14": false,
                                  "formaal-15": false,
                                  "formaal-16": false,
                                  "formaal-17": false,
                                });
                              }}
                            >
                              Tilføj flere virksomheder
                            </button>
                          )}
                        </div>
                        <div className="flex-shrink-0 mt-2 me-n4">
                          {values.tilsynEjerandele?.length !== 1 ? (
                            <button
                              className="btn btn-circle bg-light"
                              type="button"
                              aria-label="Fjern virksomhed"
                              title="Fjern vurksomhed"
                              onClick={() => arrayHelpers.remove(i)}
                            >
                              <IconDelete />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })
                }
              />
            </div>
          )}
        </div>
      </AccordionBody>
    </AccordionItem>
  );
};

export default TilsynerklaeringSection2;
